/**
 * The onboarding chat (Phase 1, interactions 1-16): radar's quiz flow in the rooms look, every line static.
 * This component wires the browser session, the BFF snapshot and the confirmations into one derived
 * transcript (buildTranscript) and draws it; it holds no flow state of its own. The website step lives in
 * `useUrlAttach`, the one-time quiz hand-off in `useQuizSubmit`, the docked step in `DockRenderer`.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AGENTS, AgentMessage, ChatComposer, ChatThread } from '@/components/rooms';
import { adDiscoveryJobKind, MAX_URL_LENGTH } from '@/contracts/onboarding-session';

import { COPY, FALLBACK_BRAND_LABEL } from '../copy';
import { onboardingKeys } from '../hooks/queryKeys';
import { useDomainAutoSend } from '../hooks/useDomainAutoSend';
import {
  useAdAccounts,
  useBrandGuide,
  useCachedAdAnalysis,
  useCompetitors,
  useConfirmAdAccounts,
  useConfirmCompetitors,
} from '../hooks/useOnboardingQueries';
import { PANEL_CLICK_GUARD_MS } from '../hooks/useClickGuard';
import { useQuizSubmit } from '../hooks/useQuizSubmit';
import { useEntranceStages, visibleAtStage } from '../hooks/useEntranceStages';
import { useSessionRecovery } from '../hooks/useSessionRecovery';
import { useSessionSnapshot } from '../hooks/useSessionSnapshot';
import { isRateLimited, useRotateOnUrlMismatch, useUrlAttach } from '../hooks/useUrlAttach';
import type { Answer, Question } from '../quiz/questions';
import { isDomain, normalizeDomain } from '../quiz/website';
import { brandLabel, buildTranscript, currentQuestion, namedSupportedPlatforms, researchPending, reviewDock } from '../state/buildTranscript';
import { deriveTasks, tasksFinished } from '../state/deriveTasks';
import { useOnboardingSession, type UseOnboardingSessionOptions } from '../state/useOnboardingSession';
import { AdsFeed, adConfirmationKey } from './AdsFeed';
import { DockRenderer } from './DockRenderer';
import { OnboardingShell, type ShellOperator } from './OnboardingShell';
import { ReconnectingBanner } from './ReconnectingBanner';
import { RestartDock } from './RestartDock';
import { TasksPanel } from './TasksPanel';
import { TranscriptView } from './TranscriptView';

export { AUTO_SEND_MS } from '../hooks/useDomainAutoSend';

export interface OnboardingChatProps {
  /** Turn first-render pacing off (tests). Reduced motion turns it off by itself. */
  paced?: boolean;
  /** Injected storage, clock and id factory (tests). */
  sessionOptions?: UseOnboardingSessionOptions;
  /**
   * The signed-in operator's identity (their email). A browser session stored by anyone else is discarded, so
   * operators sharing a browser never see each other's site and answers. Overrides `sessionOptions.ownerKey`.
   */
  ownerKey?: string | null;
  /** The signed-in operator for the sidebar's account row (name, e-mail, role label). Without it, the owner key (their e-mail) alone. */
  operator?: ShellOperator;
}

export function OnboardingChat({ paced = true, sessionOptions, ownerKey, operator }: OnboardingChatProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const domainParam = searchParams.get('domain');
  const client = useQueryClient();
  const { session, submitUrl, rejectUrl, answer, reviewId, goBack, confirmBrandProfile, complete, rotateSessionId, restart } = useOnboardingSession({
    ...sessionOptions,
    ...(ownerKey !== undefined ? { ownerKey } : {}),
  });
  const { sessionId } = session;

  const [urlDraft, setUrlDraft] = useState((domainParam ?? '').slice(0, MAX_URL_LENGTH));
  const recover = useSessionRecovery(sessionId, session.url);

  /* ---- the website, then the snapshot poll (which waits for the PUT so the two never race) ---- */
  const { handleUrl, rejected, pending, attached } = useUrlAttach({
    sessionId,
    url: session.url,
    submitUrl,
    rejectUrl,
    rotateSessionId,
  });
  const { snapshot, reconnecting, error } = useSessionSnapshot({
    sessionId,
    url: session.url,
    enabled: attached && !pending,
  });
  useRotateOnUrlMismatch(error, rotateSessionId);

  /* ---- derived flow ---- */
  const quizDone = session.url !== null && currentQuestion(session.answers) === null;
  const platforms = useMemo(() => namedSupportedPlatforms(session.answers), [session.answers]);
  // These read the BFF session, so they wait until the snapshot proves it exists (a restart re-PUTs first).
  const live = quizDone && snapshot !== null;
  const adAccounts = useAdAccounts(sessionId, live && platforms.length > 0, recover);
  const competitors = useCompetitors(sessionId, live, recover);
  // The brand guide is read as soon as the snapshot proves the session exists (not at quiz-done): the research
  // card's findings (brand profile, best sellers) land while the person is still answering. GET /brand-guide only
  // polls: it re-reads the `research` job by the ref the BFF already stored (throttled; it never calls a by-url route,
  // so it cannot start a workflow) and reads the CI brand-context draft by URL without `forceRefresh` (a plain read).
  // It starts and advances nothing upstream, so waiting for the quiz is not needed; the snapshot gate is only "the session exists".
  const brandGuide = useBrandGuide(sessionId, snapshot !== null, snapshot, recover);

  /** The site for the cards' titles: the BFF's bare host once there is a snapshot, else the address as typed. */
  const guideState = useMemo(
    () => ({ data: brandGuide.data ?? null, loading: brandGuide.isLoading, failed: brandGuide.isError }),
    [brandGuide.data, brandGuide.isLoading, brandGuide.isError],
  );

  const siteLabel = snapshot?.domain || (session.url ? normalizeDomain(session.url) : undefined);

  // A new BFF session instance (it forgot ours and we re-PUT) has none of the old reads: ask again.
  const instance = snapshot?.createdAt;
  const lastInstance = useRef(instance);
  useEffect(() => {
    if (instance === undefined || lastInstance.current === instance) return;
    if (lastInstance.current !== undefined) {
      for (const key of [onboardingKeys.adAccounts, onboardingKeys.competitors, onboardingKeys.brandGuide, onboardingKeys.adAnalysisAll]) {
        void client.invalidateQueries({ queryKey: key(sessionId) });
      }
    }
    lastInstance.current = instance;
  }, [instance, sessionId, client]);

  // The Tasks panel (tracking only): derived from the same session, snapshot and reads as the transcript.
  const adAnalysis = useCachedAdAnalysis(sessionId, 'meta');
  const tasks = useMemo(
    () => deriveTasks(session, snapshot, { adAccounts, competitors, brandGuide: guideState, adAnalysis }),
    [session, snapshot, adAccounts, competitors, guideState, adAnalysis],
  );

  const transcript = useMemo(
    () => buildTranscript(session, snapshot, { adAccounts, competitors, rejectedUrl: rejected, brandGuideName: guideState.data?.name.value ?? null }),
    [session, snapshot, adAccounts, competitors, rejected, guideState.data?.name.value],
  );
  // The sidebar's brand switcher names the prospect once there is a website (then the research's name, the brand guide's, or the
  // site's address as the last resort); before one it shows TP's own fallback.
  const sidebarBrand = session.url !== null ? brandLabel(snapshot, session.url, guideState.data?.name.value) : undefined;
  // The entrance is TP's: only a chat opened with no website yet is staged (150 / 700 / 1200 ms); a resumed one is drawn at once.
  const [fresh] = useState(() => session.url === null);
  const { stage, settled } = useEntranceStages(fresh, paced);
  const visible = useMemo(() => visibleAtStage(transcript.items, stage), [transcript.items, stage]);

  const quiz = useQuizSubmit({
    sessionId,
    live,
    quizSubmitted: snapshot?.quizSubmitted,
    instance,
    answers: session.answers,
    recover,
  });

  // Back (UI only): while the quiz is open, an answered question can be shown again, prefilled. Once the quiz is
  // done (the ad accounts, the competitors, Build) there is no way back into it: its answers may already be sent.
  const dock = useMemo(
    () => (transcript.dock?.kind === 'question' ? (reviewDock(session.answers, reviewId) ?? transcript.dock) : transcript.dock),
    [transcript.dock, session.answers, reviewId],
  );
  useDomainAutoSend({
    domain: domainParam,
    hasUrl: session.url !== null,
    askingForUrl: settled && dock?.kind === 'url',
    send: handleUrl,
  });

  const confirmAds = useConfirmAdAccounts(sessionId, recover);
  const confirmCompetitors = useConfirmCompetitors(sessionId, recover);

  // Pacing off (tests) also turns the click guard off: nothing there double-clicks.
  const clickGuardMs = paced ? PANEL_CLICK_GUARD_MS : 0;

  const startOver = () => {
    // Drop a `?domain=` prefill too, or the auto-send would put the same site straight back.
    if (domainParam !== null) setSearchParams({}, { replace: true });
    setUrlDraft('');
    restart();
  };

  const onAnswer = (question: Question, value: Answer) => answer(question.id, value);
  const onSkip = (question: Question) => answer(question.id, { selected: [], skipped: true });

  const composer =
    settled && dock?.kind === 'url' ? (
      <ChatComposer
        value={urlDraft}
        onChange={setUrlDraft}
        onSubmit={handleUrl}
        label={COPY.siteInputLabel}
        placeholder={COPY.siteInputPlaceholder}
        maxLength={MAX_URL_LENGTH}
        isSendable={isDomain}
        disabled={pending}
      />
    ) : null;
  const dockNode =
    settled && dock && dock.kind !== 'url' ? (
      <DockRenderer
        dock={dock}
        sessionId={sessionId}
        onAnswer={onAnswer}
        onSkip={onSkip}
        onBack={goBack}
        confirmAds={confirmAds}
        confirmCompetitors={confirmCompetitors}
        brandGuide={guideState}
        site={siteLabel}
        researchPending={researchPending(snapshot)}
        onConfirmBrandProfile={confirmBrandProfile}
        clickGuardMs={clickGuardMs}
      />
    ) : transcript.phase === 'complete' && settled ? (
      <RestartDock onRestart={startOver} clickGuardMs={clickGuardMs} />
    ) : null;

  // A new step in the dock (another question, the next confirm) rises in again.
  const dockKey = `${dock?.kind ?? ''}:${dock && 'question' in dock ? dock.question.id : dock?.kind === 'adAccounts' ? dock.platform : ''}`;

  return (
    <OnboardingShell
      brandName={sidebarBrand === FALLBACK_BRAND_LABEL ? undefined : sidebarBrand}
      operator={operator ?? (ownerKey ? { email: ownerKey } : undefined)}
      roomStatus={tasksFinished(tasks) ? 'completed' : 'open'}
      aside={<TasksPanel tasks={tasks} />}
    >
      {reconnecting ? <ReconnectingBanner message={isRateLimited(error) ? COPY.retryLater : COPY.reconnecting} /> : null}
      <ChatThread composer={composer} dock={dockNode} dockKey={dockKey}>
        <TranscriptView
          items={visible}
          snapshot={snapshot}
          brandGuide={guideState}
          site={siteLabel}
          competitors={competitors}
          onBuild={complete}
          clickGuardMs={clickGuardMs}
          renderAds={(platform) => (
            // Keyed on the confirmation: the BFF filters the events by the confirmed accounts, so the feed starts over.
            <AdsFeed
              key={adConfirmationKey(snapshot?.confirmations.adAccounts[platform])}
              sessionId={sessionId}
              platform={platform}
              recover={recover}
              brand={snapshot?.research.name ?? brandGuide.data?.name.value ?? null}
              site={siteLabel}
              jobState={snapshot?.jobs.find((j) => j.kind === adDiscoveryJobKind(platform))?.state}
            />
          )}
        />
        {quiz.failed ? <AgentMessage agent={AGENTS.juno} lines={[COPY.quizSaveFailed]} /> : null}
      </ChatThread>
    </OnboardingShell>
  );
}
